import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { ROLE } from '@/shared/data/permissionSystem.js'

interface Privileges {
  opportunitiesPage: boolean
  doubleUpQueue: boolean
  connectionRequests: boolean
  filterCommunityMembers: boolean
  freeCalls: {
    limitHit: boolean
    count: number
    currentCount: number
  }
  favoritedReviews: {
    limitHit: boolean
    count: number
    currentCount: number
  }
  freeCallsPerMonth: {
    limitHit: boolean
    count: number
    currentCount: number
  }
  freePaybackCalls: {
    limitHit: boolean
    count: number
    currentCount: number
  }
  giftProgramInvites: {
    limitHit: boolean
    count: number
    currentCount: number
  }
  giftRenewalLimitDate: string
  renewalLimitDate: string
}

interface Subscription {
  tier: string
  trialInterval: string
  billingCycle: string
  status: string
  type: string
  stripeConnection: string
  nextBillingPeriodStatus: string
  group: string
  customerType: string
  privileges: Privileges
}

interface AccessControl {
  accessType: string
  actorType: string
  role: string
  teamRole: string
  profileStatus: string
  mentorStatus: string
  profileVisibility: string
  subscription: Subscription
}

interface Referral {
  code: string
  link: string
  commission: number
  paypal: string | null
  giftEmailPreview: string
}

interface ProfileCompletion {
  avatar: boolean
  aboutMe: boolean
  professionTitle: boolean
  skillList: boolean
  challengeList: boolean
  interestList: boolean
  lookingForList: boolean
}

interface User {
  id: number
  name: string
  firstName: string
  lastName: string
  slug: string
  timezone: string
  email: string
  avatarUrl: string
  sessionsRecordable: boolean
  userMode: string
  role: string
  accessControl: AccessControl
  perks: {
    doubleUpQueue: boolean
  }
  profileUrl: string
  getstreamToken: string
  referral: Referral
  bonjoroIdentifyToken: string
  profileCompletion: ProfileCompletion
  invitedToTeam: string | null
  subscriptionPlanName: string
}

export const useCurrentUser = () => {
  const page = usePage<{
    currentUser: User
  }>()

  const currentUser = computed(() => page.props.currentUser)
  const timezone = computed(() => {
    const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const timezone =
      currentUser.value.accessControl.role !== ROLE.GUEST
        ? currentUser.value.timezone || defaultTimezone
        : defaultTimezone
    if (!timezone) return 'Invalid timezone'
    return timezone
  })

  return {
    currentUser,
    timezone,
  }
}
